<template>
  <div class="shop-list-page">
    <header class="page-header">
      <button
        class="back-btn"
        type="button"
        :aria-label="$t('utils.cancel')"
        @click="$router.back()"
      >
        <van-icon name="arrow-left" />
      </button>
      <h1>{{ $t('shopList.title') }}</h1>
    </header>

    <main class="shop-content">
      <section
        v-if="shops.length"
        class="shop-cards"
      >
        <article
          v-for="item in shops"
          :key="item.id"
          class="shop-card"
          role="button"
          tabindex="0"
          @click="goShop(item)"
          @keyup.enter="goShop(item)"
        >
          <div class="shop-logo">
            <img
              :src="shopImage(item)"
              alt=""
              @error="onShopImageError"
            >
          </div>
          <div class="shop-main">
            <h2>{{ item.name || '--' }}</h2>
            <div class="rating-row">
              <span class="rating-score">{{ formatScore(item.credit) }}</span>
              <span class="stars">
                <van-icon
                  v-for="index in 5"
                  :key="index"
                  :name="index <= activeStars(item.credit) ? 'star' : 'star-o'"
                  :class="{ active: index <= activeStars(item.credit) }"
                />
              </span>
            </div>
            <p class="sales-row">
              <span>{{ $t('shopList.monthSales') }}:</span>
              <strong>{{ formatSales(item.orders) }}</strong>
            </p>
          </div>
        </article>
      </section>

      <div
        v-else-if="!loading"
        class="empty-state"
      >
        {{ $t('shopList.empty') }}
      </div>
    </main>
  </div>
</template>

<script>
import Vue from 'vue';
import Fetch from '../../utils/fetch';
import { Icon } from 'vant';

Vue.use(Icon);

export default {
  name: 'Shoplist',
  data() {
    return {
      app: {},
      shops: [],
      loading: false,
      page: 1,
      limit: 50,
      shopFallback: require('../images/shop2/thumbnail.png'),
    };
  },
  created() {
    if (this.$parent && this.$parent.footer) {
      this.$parent.footer('shoplist', true);
    }
  },
  mounted() {
    this.start();
  },
  methods: {
    start() {
      Fetch('/index/webconfig').then((r) => {
        this.app = r.data || {};
      });
      this.loadShops();
    },
    loadShops() {
      if (this.loading) {
        return;
      }
      this.loading = true;
      Fetch('/index/shop_list', {
        page: this.page,
        limit: this.limit,
      }).then((r) => {
        var data = r.data || {};
        this.shops = Array.isArray(data.list) ? data.list : [];
      }).finally(() => {
        this.loading = false;
      });
    },
    goShop(item) {
      if (!item || !item.id) {
        return;
      }
      this.$router.push({
        path: '/hotel',
        query: {
          sid: item.id,
        },
      });
    },
    kefuTo() {
      if (this.app && this.app.kefu_link) {
        window.location.href = this.app.kefu_link;
      }
    },
    buildImageUrl(path, fallback) {
      if (!path) {
        return fallback;
      }
      if (/^(https?:)?\/\//.test(path) || String(path).indexOf('data:') === 0) {
        return path;
      }
      if (this.app && this.app.domain) {
        return this.app.domain.replace(/\/$/, '') + '/' + String(path).replace(/^\//, '');
      }
      return path;
    },
    shopImage(item) {
      return this.buildImageUrl(item && item.img, this.shopFallback);
    },
    onShopImageError(event) {
      if (event && event.target && event.target.src !== this.shopFallback) {
        event.target.src = this.shopFallback;
      }
    },
    scoreNumber(value) {
      var number = Number(value);
      if (!isFinite(number) || number < 0) {
        number = 0;
      }
      return Math.min(10, number / 10);
    },
    formatScore(value) {
      return this.scoreNumber(value).toFixed(1);
    },
    activeStars(value) {
      return Math.max(0, Math.min(5, Math.round(this.scoreNumber(value) / 2)));
    },
    formatSales(value) {
      var number = Number(value);
      if (!isFinite(number) || number < 0) {
        number = 0;
      }
      if (number >= 1000) {
        var shortNumber = number / 1000;
        var fixed = shortNumber >= 10 ? shortNumber.toFixed(1) : shortNumber.toFixed(1);
        return fixed.replace(/\.0$/, '') + 'k+';
      }
      return String(Math.floor(number));
    },
  },
};
</script>

<style lang="less" scoped>
.shop-list-page {
  min-height: 100vh;
  padding-bottom: 72px;
  background: #f3f4fa;
  color: #06152b;
}

.page-header {
  position: sticky;
  top: 0;
  z-index: 8;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f3f4fa;
}

.page-header h1 {
  margin: 0;
  font-size: 14px;
  line-height: 22px;
  font-weight: 500;
  color: #06152b;
}

.back-btn {
  position: absolute;
  left: 16px;
  top: 50%;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  transform: translateY(-50%);
  background: transparent;
  color: #06152b;
  font-size: 14px;
}

.shop-content {
  padding: 14px 14px 4px;
}

.shop-card {
  display: flex;
  min-height: 90px;
  margin-bottom: 10px;
  padding: 10px;
  border: 1px solid #e3e6ee;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 1px 4px rgba(15, 33, 58, 0.03);
  cursor: pointer;
  outline: none;
}

.shop-logo {
  flex: 0 0 70px;
  width: 70px;
  height: 70px;
  overflow: hidden;
  border-radius: 7px;
  background: #e9ebf2;
}

.shop-logo img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.shop-main {
  min-width: 0;
  flex: 1;
  padding: 4px 0 0 28px;
}

.shop-main h2 {
  margin: 0 0 8px;
  overflow: hidden;
  color: #06152b;
  font-size: 14px;
  line-height: 18px;
  font-weight: 500;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rating-row {
  display: flex;
  align-items: center;
  height: 20px;
  min-width: 0;
}

.rating-score {
  margin-right: 6px;
  color: #24364b;
  font-size: 14px;
  line-height: 20px;
}

.stars {
  display: inline-flex;
  align-items: center;
  min-width: 0;
}

.stars .van-icon {
  margin-right: 2px;
  color: #e3e6ed;
  font-size: 14px;
  line-height: 14px;
}

.stars .van-icon.active {
  color: #ffc400;
}

.sales-row {
  margin: 6px 0 0;
  color: #9aa2b2;
  font-size: 12px;
  line-height: 18px;
}

.sales-row strong {
  margin-left: 2px;
  color: #24364b;
  font-weight: 500;
}

.empty-state {
  padding: 90px 20px;
  color: #9aa0ad;
  font-size: 12px;
  line-height: 20px;
  text-align: center;
}

.kefu {
  position: fixed;
  right: 15px;
  bottom: 88px;
  z-index: 80;
  width: 62px;
  height: 62px;
}

.kefu_img {
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  border: 4px solid #fff;
  border-radius: 50%;
  background: linear-gradient(180deg, #1b7dff 0%, #0d80ff 64%, #fb1426 65%, #fb1426 100%);
  box-shadow: 0 6px 14px rgba(30, 70, 130, 0.25);
  color: #fff;
}

.kefu_img .van-icon {
  font-size: 31px;
}
</style>
